import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionButton from '../../components/ui/QuickActionButton';
import SearchCommandPalette from '../../components/ui/SearchCommandPalette';
import { NotificationProvider, useNotification } from '../../components/ui/NotificationToast';
import { articlesService } from '../../services/dataService';
import { t } from '../../utils/translations';
import Button from '../../components/ui/Button';

// Import page components
import ArticleFilters from './components/ArticleFilters';
import ArticleTable from './components/ArticleTable';
import BulkActionsBar from './components/BulkActionsBar';
import ArticlePreviewModal from './components/ArticlePreviewModal';
import ArticleStats from './components/ArticleStats';

const ArticlesManagementContent = () => {
  const { success, error, warning } = useNotification();

  // Layout state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Data state
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [previewArticle, setPreviewArticle] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Filter and sort state
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    status: '',
    author: '',
    language: '',
    dateFrom: '',
    dateTo: ''
  });

  const [sortConfig, setSortConfig] = useState({
    key: 'publishedAt',
    direction: 'desc'
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Fetch articles from database
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setIsLoading(true);
        const data = await articlesService.getAll();
        
        // Map database response to component expected format
        const mappedArticles = data?.map((article) => ({
          id: article?.id,
          title: article?.titleEn || article?.titleAr || article?.titleTr || t('common.untitledArticle'),
          excerpt: article?.excerptEn || article?.excerptAr || article?.excerptTr || '',
          content: article?.contentEn || article?.contentAr || article?.contentTr || '',
          category: article?.category || 'news',
          status: article?.published ? 'published' : 'draft',
          author: article?.author?.fullName || article?.author?.username || t('common.unknownAuthor'),
          publishedAt: article?.date || article?.createdAt || new Date().toISOString(),
          featuredImage: article?.imageUrl || '',
          featuredImageAlt: '',
          tags: [],
          views: 0,
          comments: 0,
          readTime: Math.ceil((article?.contentEn || article?.contentAr || article?.contentTr || '').split(' ').length / 200) || 5
        }));

        setArticles(mappedArticles);
        setFilteredArticles(mappedArticles);
      } catch (err) {
        console.error('Error fetching articles:', err);
        error(t('articles.failedToLoad'));
        setArticles([]);
        setFilteredArticles([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchArticles();
  }, [error]);

  // Filter and search logic
  useEffect(() => {
    let filtered = [...articles];

    // Search filter
    if (filters?.search) {
      const searchTerm = filters?.search?.toLowerCase();
      filtered = filtered?.filter((article) =>
      article?.title?.toLowerCase()?.includes(searchTerm) ||
      article?.excerpt?.toLowerCase()?.includes(searchTerm) ||
      article?.author?.toLowerCase()?.includes(searchTerm) ||
      article?.content?.toLowerCase()?.includes(searchTerm)
      );
    }

    // Category filter
    if (filters?.category) {
      filtered = filtered?.filter((article) => article?.category === filters?.category);
    }

    // Status filter
    if (filters?.status) {
      filtered = filtered?.filter((article) => article?.status === filters?.status);
    }

    // Author filter
    if (filters?.author) {
      filtered = filtered?.filter((article) => article?.author?.toLowerCase()?.includes(filters?.author?.toLowerCase()));
    }

    // Date range filter
    if (filters?.dateFrom) {
      filtered = filtered?.filter((article) => new Date(article.publishedAt) >= new Date(filters.dateFrom));
    }
    if (filters?.dateTo) {
      filtered = filtered?.filter((article) => new Date(article.publishedAt) <= new Date(filters.dateTo));
    }

    // Sort articles
    filtered?.sort((a, b) => {
      const aValue = a?.[sortConfig?.key];
      const bValue = b?.[sortConfig?.key];

      if (sortConfig?.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredArticles(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [articles, filters, sortConfig]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e?.ctrlKey || e?.metaKey) && e?.key === 'k') {
        e?.preventDefault();
        setIsSearchOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Event handlers
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      category: '',
      status: '',
      author: '',
      language: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectionChange = (selectedIds) => {
    setSelectedArticles(selectedIds);
  };

  const handleEdit = (article) => {
    window.location.href = `/article-editor?id=${article?.id}`;
  };

  const handlePreview = (article) => {
    setPreviewArticle(article);
    setIsPreviewOpen(true);
  };

  const handleDuplicate = (article) => {
    // Note: This creates a client-side duplicate. To persist, would need to create in database
    const duplicatedArticle = {
      ...article,
      id: Date.now(),
      title: `${article?.title} (${t('articles.copy')})`,
      status: 'draft',
      publishedAt: new Date()?.toISOString()
    };

    setArticles((prev) => [duplicatedArticle, ...prev]);
    setFilteredArticles((prev) => [duplicatedArticle, ...prev]);
    success(t('articles.hasBeenDuplicated').replace('{title}', article?.title));
  };

  const handleStatusToggle = async (article) => {
    try {
      const newStatus = article?.status === 'published' ? 'draft' : 'published';
      await articlesService.update(article?.id, {
        ...article,
        published: newStatus === 'published'
      });

      setArticles((prev) => prev?.map((a) =>
      a?.id === article?.id ?
      { ...a, status: newStatus } :
      a
      ));

      success(newStatus === 'published' ? t('articles.hasBeenPublished').replace('{title}', article?.title) : t('articles.hasBeenUnpublished').replace('{title}', article?.title));
    } catch (err) {
      console.error('Error updating article status:', err);
      error(t('articles.failedToUpdateStatus'));
    }
  };

  const handleBulkAction = async (action) => {
    const selectedCount = selectedArticles?.length;

    try {
      switch (action) {
        case 'publish':
          await Promise.all(
            selectedArticles.map(id => {
              const article = articles.find(a => a.id === id);
              return article ? articlesService.update(id, { ...article, published: true }) : Promise.resolve();
            })
          );
          setArticles((prev) => prev?.map((article) =>
          selectedArticles?.includes(article?.id) ?
          { ...article, status: 'published' } :
          article
          ));
          success(t('articles.haveBeenPublished').replace('{count}', selectedCount));
          break;

        case 'unpublish':
          await Promise.all(
            selectedArticles.map(id => {
              const article = articles.find(a => a.id === id);
              return article ? articlesService.update(id, { ...article, published: false }) : Promise.resolve();
            })
          );
          setArticles((prev) => prev?.map((article) =>
          selectedArticles?.includes(article?.id) ?
          { ...article, status: 'draft' } :
          article
          ));
          success(t('articles.haveBeenUnpublished').replace('{count}', selectedCount));
          break;

        case 'draft':
          await Promise.all(
            selectedArticles.map(id => {
              const article = articles.find(a => a.id === id);
              return article ? articlesService.update(id, { ...article, published: false }) : Promise.resolve();
            })
          );
          setArticles((prev) => prev?.map((article) =>
          selectedArticles?.includes(article?.id) ?
          { ...article, status: 'draft' } :
          article
          ));
          success(t('articles.haveBeenMovedToDraft').replace('{count}', selectedCount));
          break;

        case 'delete':
          await Promise.all(selectedArticles.map(id => articlesService.delete(id)));
          setArticles((prev) => prev?.filter((article) => !selectedArticles?.includes(article?.id)));
          setFilteredArticles((prev) => prev?.filter((article) => !selectedArticles?.includes(article?.id)));
          success(t('articles.haveBeenDeleted').replace('{count}', selectedCount));
          break;

        case 'export':
          // Export functionality
          const exportData = articles?.filter((article) => selectedArticles?.includes(article?.id));
          console.log('Exporting articles:', exportData);
          success(t('articles.haveBeenExported').replace('{count}', selectedCount));
          break;

        case 'duplicate':
          // Note: Duplication would require creating new articles in database
          // For now, this is a client-side operation
          const duplicatedArticles = articles?.filter((article) => selectedArticles?.includes(article?.id))?.map((article) => ({
            ...article,
            id: Date.now() + Math.random(),
            title: `${article?.title} (${t('articles.copy')})`,
            status: 'draft',
            publishedAt: new Date()?.toISOString()
          }));

          setArticles((prev) => [...duplicatedArticles, ...prev]);
          setFilteredArticles((prev) => [...duplicatedArticles, ...prev]);
          success(t('articles.haveBeenDuplicated').replace('{count}', selectedCount));
          break;

        default:
          warning(t('articles.unknownAction'));
      }
    } catch (err) {
      console.error('Error performing bulk action:', err);
      error(t('articles.failedToPerformAction'));
    }

    setSelectedArticles([]);
  };

  // Pagination
  const totalPages = Math.ceil(filteredArticles?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedArticles = filteredArticles?.slice(startIndex, startIndex + itemsPerPage);

  return (
    <div className="min-h-screen bg-background">
      <Header
        onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMenuOpen={isMobileMenuOpen} />

      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />

      <main className={`transition-all duration-300 ${
      isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'} pt-16`
      }>
        <div className="p-6">
          {/* Page Header */}
          <div className="mb-6">
            <Breadcrumbs />
            
            <div className="flex items-center justify-between mt-4">
              <div>
                <h1 className="text-2xl font-bold text-foreground mb-2">
                  {t('articles.management')}
                </h1>
                <p className="text-muted-foreground">
                  {t('articles.managementDescription')}
                </p>
              </div>

              <div className="flex items-center space-x-3 space-x-reverse">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsSearchOpen(true)}
                  iconName="Search"
                  iconPosition="left"
                  iconSize={16}>

                  {t('common.search')}
                  <kbd className="ml-2 px-2 py-1 bg-muted rounded text-xs">⌘K</kbd>
                </Button>

                <Button
                  variant="default"
                  onClick={() => window.location.href = '/article-editor'}
                  iconName="Plus"
                  iconPosition="left"
                  iconSize={16}>

                  {t('articles.createArticle')}
                </Button>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-pulse space-y-4">
                <div className="h-8 bg-muted rounded w-1/3 mx-auto"></div>
                <div className="h-32 bg-muted rounded"></div>
                <div className="h-64 bg-muted rounded"></div>
              </div>
            </div>
          ) : (
            <>
              {/* Statistics */}
              <ArticleStats articles={articles} />

              {/* Filters */}
              <ArticleFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                totalCount={articles?.length}
                filteredCount={filteredArticles?.length}
                onClearFilters={handleClearFilters} />


              {/* Articles Table */}
              <ArticleTable
                articles={paginatedArticles}
                selectedArticles={selectedArticles}
                onSelectionChange={handleSelectionChange}
                onEdit={handleEdit}
                onPreview={handlePreview}
                onDuplicate={handleDuplicate}
                onStatusToggle={handleStatusToggle}
                onSort={handleSort}
                sortConfig={sortConfig} />


              {/* Pagination */}
              {totalPages > 1 &&
              <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-muted-foreground">
                    {t('common.showing')} {startIndex + 1} {t('common.to')} {Math.min(startIndex + itemsPerPage, filteredArticles?.length)} {t('common.of')} {filteredArticles?.length} {t('articles.articles')}
                  </div>

                  <div className="flex items-center space-x-2 space-x-reverse">
                    <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    iconName="ChevronRight"
                    iconSize={16}>

                      {t('common.previous')}
                    </Button>

                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const pageNum = i + 1;
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? 'default' : 'ghost'}
                          size="sm"
                          onClick={() => setCurrentPage(pageNum)}
                          className="w-8 h-8">

                            {pageNum}
                          </Button>);

                    })}
                    </div>

                    <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    iconName="ChevronLeft"
                    iconSize={16}>

                      {t('common.next')}
                    </Button>
                  </div>
                </div>
              }
            </>
          )}
        </div>
      </main>
      {/* Bulk Actions Bar */}
      <BulkActionsBar
        selectedCount={selectedArticles?.length}
        onBulkAction={handleBulkAction}
        onClearSelection={() => setSelectedArticles([])} />

      {/* Article Preview Modal */}
      <ArticlePreviewModal
        article={previewArticle}
        isOpen={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewArticle(null);
        }}
        onEdit={handleEdit} />

      {/* Quick Action Button */}
      <QuickActionButton />
      {/* Search Command Palette */}
      <SearchCommandPalette
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)} />

    </div>);

};

const ArticlesManagement = () => {
  return (
    <NotificationProvider>
      <ArticlesManagementContent />
    </NotificationProvider>);

};

export default ArticlesManagement;